import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  Toggle,
  ContextNotification,
  TabNavigation,
  TabNavigationItem,
} from '@pandiafi/components'
import { Button, Tooltip } from '@pandiafi/design-system'
import { DateTime } from 'luxon'

import type { TimeSlotForResident } from '../../../common/validations/reservationCalendarSchemas'
import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'
import { useAPIGetContractSuspense } from '../../api/apiHooks/useAPIGetContract'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { ReservationActionSuccess } from './ReservationActionSuccess'
import { useAPICreateReservation } from '../../api/apiHooks/useAPICreateReservation'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'
import i18n from '../../i18n/i18n'
import { useAPIInitSingleReservationPayment } from '../../api/apiHooks/useAPIInitSingleReservationPayment'
import { formatPrice } from './utils'
import { getCategoryModuleConfig } from '../../../common/constants/reservationCategories'
import type { ReservationCalendarForResident, PriceInfoForDate } from './types'
import { formatDate, formatTimeRange } from '../../utils/dateUtils'
import { ReservationRulesAndTermsLinks } from '../../components/ReservationRulesAndTermsLinks/ReservationRulesAndTermsLinks'
import { ReservationTermsAcceptance } from '../../components/ReservationTermsAcceptance/ReservationTermsAcceptance'
import { ReservationRulesAndTermsLink } from '../../components/ReservationRulesAndTermsLinks/ReservationRulesAndTermsLinks'

interface ReservationModalProps {
  calendar: ReservationCalendarForResident | null
  timeSlots: TimeSlotForResident[]
  priceInfo: PriceInfoForDate | undefined
  onClose: () => void
}

export const ReservationModal: React.FC<ReservationModalProps> = ({
  calendar,
  timeSlots,
  priceInfo,
  onClose,
}) => {
  const { t } = useTranslation(['reservations'])
  const { currentContractId } = useCurrentContract()
  const { data: contract } = useAPIGetContractSuspense(currentContractId)

  const [isRecurring, setIsRecurring] = useState(
    calendar?.allowRecurringReservations ?? false
  )
  const [rulesAccepted, setRulesAccepted] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const moduleConfig = useModuleConfig()

  const SEND_CONFIRMATION_EMAIL_KEY = 'reservation-send-confirmation-email'
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(() => {
    const stored = localStorage.getItem(SEND_CONFIRMATION_EMAIL_KEY)
    return stored === null ? true : stored === 'true'
  })

  const createReservationMutation = useAPICreateReservation(currentContractId)
  const createSingleReservationMutation =
    useAPIInitSingleReservationPayment(currentContractId)

  useEffect(() => {
    setShowSuccess(false)
    setRulesAccepted(false)
    setIsRecurring(calendar?.allowRecurringReservations ?? false)
    setErrorMsg(null)
  }, [timeSlots, calendar])

  const currentReservationCount = calendar?.currentReservationCount ?? 0
  const hasReachedLimit =
    calendar?.maxReservedSlotsPerWeek !== null &&
    calendar?.maxReservedSlotsPerWeek !== undefined &&
    currentReservationCount >= calendar.maxReservedSlotsPerWeek

  if (!calendar || timeSlots.length === 0 || !priceInfo) return null

  const firstSlot = timeSlots[0]
  const lastSlot = timeSlots[timeSlots.length - 1]
  const slotDateTime = DateTime.fromISO(firstSlot.startsAt)
  const priceNoticeMonths = 3

  const weekdayName = slotDateTime.toFormat('cccc', { locale: i18n.language })
  const dateFormatted = formatDate(firstSlot.startsAt)
  const timeRange = formatTimeRange(firstSlot.startsAt, lastSlot.endsAt)

  const contractEndDate = contract.endDate ?? null
  const slotStartDate = DateTime.fromISO(firstSlot.startsAt).toISODate()
  const isAfterContractEnd =
    contractEndDate && slotStartDate ? slotStartDate > contractEndDate : false

  const resolveErrorMessage = (err: unknown) => {
    if (err instanceof ResidentAppError) {
      switch (err.errorCode) {
        case ResidentAppErrorCode.RESERVATION_OPEN_ENDED_NOT_ALLOWED:
          return t('error.contractContinuousNotAllowed')
        case ResidentAppErrorCode.RESERVATION_END_AFTER_CONTRACT_END:
          return t('error.contractEndDate')
        case ResidentAppErrorCode.CONTRACT_END_BEFORE_RESERVATION_START:
          return t('error.contractEndBeforeReservationStart')
        case ResidentAppErrorCode.RESERVATION_END_BEFORE_START:
          return t('error.reservationEndBeforeStart')
        case ResidentAppErrorCode.RESERVATION_START_BEFORE_CONTRACT_START:
          return t('error.reservationStartBeforeContractStart')
        case ResidentAppErrorCode.CONFLICT:
          return t('error.conflict')
        case ResidentAppErrorCode.RESERVATION_MAX_RESERVED_SLOTS_PER_WEEK:
          return t('error.maxReservedSlotsPerWeek')
        case ResidentAppErrorCode.RESERVATION_INVALID_SLOT_GRID_POSITION:
          return t('error.invalidSlotGridPosition')
        case ResidentAppErrorCode.RESERVATION_ON_STARTED_SLOTS_NOT_ALLOWED:
          return t('error.slotAlreadyStarted')
        case ResidentAppErrorCode.INTERNAL_SERVER_ERROR:
          return t('error.reservationFailed')
        default:
          break
      }
    }

    return t('error.reservationFailed')
  }

  const moduleLinks = getCategoryModuleConfig(
    calendar?.reservableCategory,
    moduleConfig
  )

  const contractDocumentLink = moduleLinks?.contractDocumentLink
  const termsOfUseDocumentLink = moduleLinks?.termsOfUseDocumentLink

  const fallbackLinks: ReservationRulesAndTermsLink[] = []
  if (contractDocumentLink) {
    fallbackLinks.push({
      href: contractDocumentLink,
      label: t('reservations:modal.saunaRulesLink'),
    })
  }
  if (termsOfUseDocumentLink) {
    fallbackLinks.push({
      href: termsOfUseDocumentLink,
      label: t('reservations:modal.commonAreaTermsLink'),
    })
  }

  const handleConfirm = () => {
    if (!calendar) return
    setErrorMsg(null)

    createReservationMutation.mutate(
      {
        timeSlotIds: timeSlots.map((slot) => slot.id),
        reservationCalendarId: calendar.id,
        validUntil: isRecurring ? contract.endDate : undefined,
        sendConfirmationEmail,
      },
      {
        onSuccess: () => {
          setShowSuccess(true)
        },
        onError: (err) => {
          setErrorMsg(resolveErrorMessage(err))
        },
      }
    )
  }

  const handlePayment = () => {
    if (!calendar) return
    setErrorMsg(null)

    createSingleReservationMutation.mutate(
      {
        reservation: {
          timeSlotIds: timeSlots.map((slot) => slot.id),
          reservationCalendarId: calendar.id,
        },
        paymentInformation: {
          language: i18n.language.toUpperCase() as unknown as
            | 'FI'
            | 'SV'
            | 'EN',
          returnUrl: window.location.href,
        },
        sendConfirmationEmail,
      },
      {
        onSuccess: (data) => {
          if (data.accepted) {
            setShowSuccess(true)
          } else if (data.href) {
            window.location.href = data.href
          } else {
            setErrorMsg(t('error.reservationFailed'))
          }
        },
        onError: (err) => {
          setErrorMsg(resolveErrorMessage(err))
        },
      }
    )
  }

  const renderActionButton = ({
    label,
    onClick,
    isLoading,
    isDisabled,
  }: {
    label: string
    onClick: () => void
    isLoading: boolean
    isDisabled: boolean
  }) => {
    const button = (
      <Button
        variant="primary"
        onClick={onClick}
        disabled={isDisabled}
        loading={isLoading}
      >
        {label}
      </Button>
    )

    if (!isAfterContractEnd) return button

    return (
      <Tooltip
        tooltipClassName="mx-0 w-64 text-left"
        triggerArea={<span className="inline-block">{button}</span>}
      >
        {t('error.contractEndDateStart')}
      </Tooltip>
    )
  }

  const modalKey = `${timeSlots[0]?.id}-${timeSlots[0]?.startsAt}-${timeSlots.length}`
  const modalRenderKey = `${modalKey}-${showSuccess ? 'success' : 'form'}`

  const price = isRecurring ? priceInfo.monthlyPrice : priceInfo.oncePrice
  const priceValue = price !== null ? parseFloat(price) : null
  const hasComplimentarySlotsRemaining =
    !isRecurring &&
    calendar.complimentarySlotsPerWeek > 0 &&
    calendar.usedComplimentarySlotsThisWeek < calendar.complimentarySlotsPerWeek
  const isSingleFree =
    !isRecurring &&
    (hasComplimentarySlotsRemaining ||
      (priceValue === 0 && calendar.complimentarySlotsPerWeek === 0))
  const shouldShowPrice =
    !hasComplimentarySlotsRemaining &&
    priceValue !== null &&
    !Number.isNaN(priceValue) &&
    priceValue > 0
  const isActionPending = isRecurring
    ? createReservationMutation.isPending
    : createSingleReservationMutation.isPending

  const hideAcceptRules =
    isAfterContractEnd || isActionPending || hasReachedLimit
  const isDisabled = !rulesAccepted || hideAcceptRules

  return (
    <Modal
      show
      onClose={onClose}
      title={t('modal.title')}
      key={modalRenderKey}
      headerElement={
        showSuccess ? (
          <div>
            <div className="border border-gray-700 rounded-lg overflow-hidden">
              <ContextNotification type="success" textAlign="center">
                {isRecurring
                  ? t('timeFormatting.recurringType')
                  : t('timeFormatting.singleType')}
              </ContextNotification>
            </div>
          </div>
        ) : (
          <TabNavigation
            selectedTab={isRecurring ? 'recurring' : 'single'}
            fullwidth
            onChange={(tab) => {
              const isTabDisabled =
                (tab === 'recurring' && !calendar.allowRecurringReservations) ||
                (tab === 'single' && !calendar.allowSingleReservations)

              if (isTabDisabled) return

              setIsRecurring(tab === 'recurring')
            }}
          >
            <TabNavigationItem
              id="recurring"
              disabled={calendar.allowRecurringReservations === false}
            >
              {t('modal.recurringReservation')}
            </TabNavigationItem>
            <TabNavigationItem
              id="single"
              disabled={calendar.allowSingleReservations === false}
            >
              {t('modal.singleReservation')}
            </TabNavigationItem>
          </TabNavigation>
        )
      }
    >
      {showSuccess ? (
        <ReservationActionSuccess
          calendar={calendar}
          isRecurring={isRecurring}
          action="create"
          onClose={onClose}
          reservation={{
            startsAt: firstSlot.startsAt,
            endsAt: lastSlot.endsAt,
          }}
        />
      ) : (
        <>
          <Modal.Content>
            {errorMsg && (
              <ContextNotification type="error" className="mb-4">
                {errorMsg}
              </ContextNotification>
            )}

            <div className="text-lg mb-2">
              {calendar.name} {isRecurring ? t('timeFormatting.everyWeek') : ''}{' '}
              <span className="font-bold">
                {t(
                  isRecurring
                    ? 'timeFormatting.weekdayWithTimeRange'
                    : 'timeFormatting.weekdayDateWithTimeRange',
                  {
                    weekday: weekdayName,
                    date: dateFormatted,
                    timeRange,
                  }
                )}
              </span>
            </div>
            <div className="mb-6">
              {(() => {
                if (!shouldShowPrice) return null

                const priceForFormat = isRecurring
                  ? priceInfo.monthlyPrice
                  : priceInfo.oncePrice
                const upcomingPrice = isRecurring
                  ? priceInfo.upcomingRecurringUnitPrice
                  : null
                const upcomingPriceValidFrom = isRecurring
                  ? priceInfo.upcomingRecurringUnitPriceValidFrom
                  : null

                const formattedPrice = formatPrice(priceForFormat)
                const formattedUpcomingPrice = formatPrice(upcomingPrice)
                const noticeWindowEnd = slotDateTime
                  .startOf('day')
                  .plus({ months: priceNoticeMonths })
                  .endOf('day')
                  .toISODate()
                const isWithinNoticeWindow =
                  upcomingPriceValidFrom &&
                  noticeWindowEnd &&
                  upcomingPriceValidFrom <= noticeWindowEnd
                const showUpcomingPrice =
                  formattedUpcomingPrice &&
                  formattedUpcomingPrice !== formattedPrice &&
                  isWithinNoticeWindow

                return formattedPrice ? (
                  <div>
                    <span className="font-semibold">
                      {t(
                        isRecurring
                          ? 'timeFormatting.recurringUnitPrice'
                          : 'timeFormatting.singleUnitPrice',
                        {
                          price: formattedPrice,
                        }
                      )}
                    </span>{' '}
                    {isRecurring ? (
                      <>
                        {t('timeFormatting.priceAddedToContractFromDate', {
                          date: slotDateTime.toFormat('d.M.yyyy'),
                        })}
                      </>
                    ) : (
                      <>{t('timeFormatting.payOn3rdParty')}.</>
                    )}
                    {showUpcomingPrice && (
                      <p>
                        {t('timeFormatting.upcomingRecurringUnitPrice', {
                          price: formattedUpcomingPrice,
                          date: formatDate(upcomingPriceValidFrom),
                        })}
                      </p>
                    )}
                  </div>
                ) : null
              })()}
            </div>
            {hasReachedLimit && (
              <ContextNotification type="warning" className="mb-4">
                {t('modal.reservationLimitWarning')}
              </ContextNotification>
            )}
            {isAfterContractEnd && (
              <ContextNotification type="warning" className="mb-4">
                {t('error.contractEndDateStart')}
              </ContextNotification>
            )}
            {hideAcceptRules ? (
              <div className="mb-6">
                <ReservationRulesAndTermsLinks
                  contractId={currentContractId}
                  reservableCategory={calendar.reservableCategory}
                  realPropertyHierarchyId={calendar.realPropertyHierarchyId}
                  fallbackLinks={fallbackLinks}
                />
              </div>
            ) : (
              <>
                <ReservationTermsAcceptance
                  contractId={currentContractId}
                  reservableCategory={calendar.reservableCategory}
                  realPropertyHierarchyId={calendar.realPropertyHierarchyId}
                  fallbackLinks={fallbackLinks}
                  accepted={rulesAccepted}
                  onAcceptedChange={setRulesAccepted}
                >
                  {t('modal.acceptRules')}
                </ReservationTermsAcceptance>
                {moduleConfig.reservationConfirmationEnabled && (
                  <Toggle
                    className="mt-4"
                    checked={sendConfirmationEmail}
                    onChange={(val) => {
                      setSendConfirmationEmail(val)
                      localStorage.setItem(
                        SEND_CONFIRMATION_EMAIL_KEY,
                        String(val)
                      )
                    }}
                    labelPosition="right"
                  >
                    {t('modal.sendConfirmationEmail')}
                  </Toggle>
                )}
              </>
            )}
            {moduleConfig.reservationConfirmationEnabled &&
              !sendConfirmationEmail && (
                <ContextNotification type="warning" className="mt-4">
                  {shouldShowPrice && !isRecurring
                    ? t('modal.confirmationEmailOffWarningPaid')
                    : t('modal.confirmationEmailOffWarningFree')}
                </ContextNotification>
              )}
          </Modal.Content>

          <Modal.Buttons>
            <Button variant="text" onClick={onClose}>
              {t('modal.cancel')}
            </Button>
            {isRecurring
              ? renderActionButton({
                  label: t('modal.confirm'),
                  onClick: handleConfirm,
                  isLoading: createReservationMutation.isPending,
                  isDisabled,
                })
              : isSingleFree
                ? renderActionButton({
                    label: t('modal.reserveButton'),
                    onClick: handlePayment,
                    isLoading: createReservationMutation.isPending,
                    isDisabled,
                  })
                : renderActionButton({
                    label: t('modal.paymentButton'),
                    onClick: handlePayment,
                    isLoading: createSingleReservationMutation.isPending,
                    isDisabled,
                  })}
          </Modal.Buttons>
        </>
      )}
    </Modal>
  )
}
